import { z } from 'zod';
import { RESEARCH_ROLE_KEYS } from '../constants/onboardingProfile';
/**
 * Centralized validation schemas using Zod
 * This is the single source of truth for all validation rules across the application
 */
// Common validation patterns
const uuidSchema = z.guid('Invalid UUID format');
const emailSchema = z.email('Invalid email format');
const timestampSchema = z.union([z.iso.datetime(), z.date()]);
const nonEmptyStringSchema = z.string().min(1, 'This field is required');
// User Settings Schema
export const UserSettingsSchema = z.object({
    theme: z.enum(['light', 'dark', 'system'], {
        error: () => 'Theme must be light, dark, or system',
    }),
    language: z.string().min(2, 'Language code must be at least 2 characters'),
    timezone: z.string().min(1, 'Timezone is required'),
    notifications: z.object({
        email: z.boolean(),
        push: z.boolean(),
        projectUpdates: z.boolean(),
        collaboratorMessages: z.boolean(),
    }),
    defaultWorkspaceId: z.guid().optional(),
});
// User Document Schema
export const UserSchema = z.object({
    id: uuidSchema,
    email: emailSchema,
    name: z
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name cannot exceed 100 characters'),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    settings: UserSettingsSchema,
    subscription: z.enum(['free', 'paid'], {
        error: () => 'Subscription must be free or paid',
    }),
    workspaces: z.array(z.guid()),
    profileImageUrl: z.url().optional(),
});
// Canonical ORCID iD form: 16 digits in groups of 4, last group's
// 4th character may be ``X`` (the ISO 7064 mod-11 checksum value).
const ORCID_ID_PATTERN = /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/;
/**
 * Allow-list schema for ``PATCH /api/users/:userId``. Rejects every
 * field not in this list — repository ``update`` would otherwise pass
 * arbitrary keys through to Prisma, allowing an authenticated caller
 * to overwrite ``firebaseUid`` / ``email`` / etc.
 *
 * ``.strict()`` is the load-bearing line here: it makes Zod throw on
 * unknown keys instead of silently dropping them.
 */
export const UpdateUserSchema = z.strictObject({
    name: z
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name cannot exceed 100 characters')
        .optional(),
    avatar: z.url().max(2048).optional(),
    /**
     * Public ORCID iD in canonical 16-digit form. ``''`` and ``null``
     * both signal "clear the saved iD" — the controller normalises
     * either to ``null`` before calling the service.
     */
    orcidId: z
        .union([
        z.literal(''),
        z.null(),
        z
            .string()
            .regex(ORCID_ID_PATTERN, 'ORCID iD must be in canonical 16-digit form, e.g. "0000-0001-2345-6789".'),
    ])
        .optional(),
    /**
     * Personal research role (onboarding profile). Enum key from
     * RESEARCH_ROLE_OPTIONS; `''`/`null` clears it. PERSONAL — self-editable
     * only (the update route already scopes to the requesting user).
     */
    researchRole: z.union([z.literal(''), z.null(), z.enum(RESEARCH_ROLE_KEYS)]).optional(),
    /** Free-text role when researchRole = 'other'. UGC; length-capped. */
    researchRoleOther: z.union([z.literal(''), z.null(), z.string().trim().max(200)]).optional(),
});
// Workspace Document Schema
export const WorkspaceSchema = z.object({
    id: uuidSchema,
    name: z
        .string()
        .min(2, 'Workspace name must be at least 2 characters')
        .max(100, 'Workspace name cannot exceed 100 characters'),
    institution: z
        .string()
        .min(2, 'Institution name must be at least 2 characters')
        .max(200, 'Institution name cannot exceed 200 characters'),
    description: z.string().max(1000, 'Description cannot exceed 1000 characters').optional(),
    // `createdById` (not `createdBy`) — see WorkspaceDoc in types/models.ts.
    // `memberIds` removed: no endpoint has ever returned it.
    createdById: uuidSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    stats: z.object({
        memberCount: z.int().min(0),
        projectCount: z.int().min(0),
        noteCount: z.int().min(0),
        storageUsedBytes: z.int().min(0),
    }),
});
// Project Document Schema
export const ProjectSchema = z.object({
    id: uuidSchema,
    name: z
        .string()
        .min(3, 'Project name must be at least 3 characters')
        .max(100, 'Project name cannot exceed 100 characters'),
    description: z.string().max(1000, 'Project description cannot exceed 1000 characters').optional(),
    workspaceId: uuidSchema,
    templateId: z.guid().optional(),
    tags: z
        .array(z.string().max(50, 'Tag cannot exceed 50 characters'))
        .max(20, 'Cannot have more than 20 tags')
        .default([]),
    createdById: uuidSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    collaborators: z.array(z.guid()).default([]),
    metadata: z.record(z.string(), z.any()).default({}),
    stats: z.object({
        noteCount: z.int().min(0).default(0),
        lastActivity: timestampSchema,
    }),
    deadlines: z
        .object({
        start: timestampSchema.optional(),
        end: timestampSchema.optional(),
        milestones: z
            .array(z.object({
            name: z.string().min(1, 'Milestone name is required').max(100),
            date: timestampSchema,
            completed: z.boolean().default(false),
        }))
            .default([]),
    })
        .optional(),
});
// Note Document Schema
export const NoteSchema = z.object({
    id: uuidSchema,
    content: z
        .string()
        .min(1, 'Note content is required')
        .max(2000, 'Note content cannot exceed 2000 characters'),
    type: z.enum(['text', 'voice', 'photo'], {
        error: () => 'Note type must be text, voice, or photo',
    }),
    workspaceId: uuidSchema,
    projectId: uuidSchema,
    originalContent: z.string().optional(),
    editedContent: z.string().optional(),
    attachments: z
        .array(z.object({
        id: uuidSchema,
        url: z.url(),
        type: z.enum(['image', 'audio', 'video', 'document']),
        filename: z.string().min(1, 'Filename is required'),
        size: z.int().min(0),
        mimeType: z.string().min(1, 'MIME type is required'),
        metadata: z.record(z.string(), z.any()).default({}),
        uploadedAt: timestampSchema,
    }))
        .default([]),
    tags: z
        .array(z.string().max(50, 'Tag cannot exceed 50 characters'))
        .max(20, 'Cannot have more than 20 tags')
        .default([]),
    capturedBy: uuidSchema,
    capturedAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    aiProcessed: z.boolean().default(false),
    extractedData: z.record(z.string(), z.any()).optional(),
    searchTokens: z.array(z.string()).optional(),
    embedding: z.array(z.number()).optional(),
    version: z.int().min(1).default(1),
    previousVersions: z
        .array(z.object({
        version: z.int().min(1),
        content: nonEmptyStringSchema,
        updatedAt: timestampSchema,
        updatedBy: uuidSchema,
    }))
        .optional(),
});
// Field Definition Schema for Templates
export const FieldDefinitionSchema = z.object({
    id: z.string().min(1, 'Field ID is required'),
    name: z
        .string()
        .min(1, 'Field name is required')
        .max(100, 'Field name cannot exceed 100 characters'),
    type: z.enum(['text', 'number', 'date', 'select', 'multiselect', 'boolean', 'textarea'], {
        error: () => 'Invalid field type',
    }),
    required: z.boolean().default(false),
    defaultValue: z.any().optional(),
    options: z.array(z.string()).optional(),
    validation: z
        .object({
        min: z.number().optional(),
        max: z.number().optional(),
        pattern: z.string().optional(),
    })
        .optional(),
});
// Template Document Schema
export const TemplateSchema = z.object({
    id: uuidSchema,
    name: z
        .string()
        .min(2, 'Template name must be at least 2 characters')
        .max(100, 'Template name cannot exceed 100 characters'),
    description: z
        .string()
        .min(10, 'Template description must be at least 10 characters')
        .max(500, 'Template description cannot exceed 500 characters'),
    type: z.enum(['system', 'workspace'], {
        error: () => 'Template type must be system or workspace',
    }),
    category: z.enum(['daily_log', 'experiment_protocol', 'sample_analysis', 'custom'], {
        error: () => 'Invalid template category',
    }),
    workspaceId: z.guid().optional(),
    structure: z.object({
        projectFields: z.array(FieldDefinitionSchema).default([]),
        noteFields: z.array(FieldDefinitionSchema).default([]),
    }),
    createdBy: z.guid().optional(),
    sourceProjectId: z.guid().optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    usageCount: z.int().min(0).default(0),
    isPublic: z.boolean().default(false),
    version: z.int().min(1).default(1),
});
// Validation helper functions
export const validateUser = (data) => UserSchema.safeParse(data);
export const validateWorkspace = (data) => WorkspaceSchema.safeParse(data);
export const validateProject = (data) => ProjectSchema.safeParse(data);
export const validateNote = (data) => NoteSchema.safeParse(data);
export const validateTemplate = (data) => TemplateSchema.safeParse(data);
// Partial validation helpers for updates
export const validatePartialProject = (data) => ProjectSchema.partial().safeParse(data);
export const validatePartialNote = (data) => NoteSchema.partial().safeParse(data);
// Create validation helpers
export const validateProjectCreate = (data) => ProjectSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    stats: true,
    workspaceId: true,
    createdById: true,
    collaborators: true,
    metadata: true,
    deadlines: true,
    templateId: true,
}).safeParse(data);
export const validateNoteCreate = (data) => NoteSchema.omit({ id: true, createdAt: true, updatedAt: true, version: true }).safeParse(data);
// Field-specific validation helpers
export const getFieldValidation = (entityType, fieldName) => {
    const schemas = {
        project: ProjectSchema,
        note: NoteSchema,
    };
    const schema = schemas[entityType];
    if (!schema || !(fieldName in schema.shape)) {
        return null;
    }
    return schema.shape[fieldName];
};
// Validation error formatting
export const formatValidationErrors = (errors) => {
    return errors.issues.map((error) => ({
        field: error.path.join('.'),
        message: error.message,
        code: error.code,
    }));
};
// Type guards
export const isValidUUID = (value) => {
    return uuidSchema.safeParse(value).success;
};
export const isValidEmail = (value) => {
    return emailSchema.safeParse(value).success;
};
//# sourceMappingURL=validation.js.map